import type { Message } from "@ag-ui/core";
import { apiClient, ApiError, apiMutate, isApiError } from "~/utils/apiClient";
import type { PartialAgent } from "./agents";

const { VITE_ANALYSIS_AGENT_API, VITE_CONVERSATIONS_API } = import.meta.env;

export const ANALYSIS_AGENT_API_PREFIX = VITE_ANALYSIS_AGENT_API;

export const conversationPath = (id: string) => `${VITE_ANALYSIS_AGENT_API}/conversations/${id}`;

export const conversationConnectPath = (id: string) =>
  `${VITE_ANALYSIS_AGENT_API}/conversations/${id}/connect`;

export const conversationAguiUrl = (id: string) =>
  `${VITE_ANALYSIS_AGENT_API}/conversations/${id}/messages_agui`;

export const conversationCancelPath = (id: string) =>
  `${VITE_ANALYSIS_AGENT_API}/conversations/${id}/cancel`;

export const conversationNamePath = (id: string) =>
  `${VITE_ANALYSIS_AGENT_API}/conversations/${id}/name`;

export const conversationPlanPath = (id: string) =>
  `${VITE_ANALYSIS_AGENT_API}/conversations/${id}/plan`;

// One step of the data-prep agent's durable plan, as the store serves it: the
// harness `PlanItem` shape, snake_case on the wire. Kept as raw JSON (not
// camelized) so the shared plan mapper can normalize it the same way it does
// the message-derived plan.
export interface PlanItemWire {
  id: string;
  content: string;
  status: string;
  active_form: string;
  parent_id: string | null;
}

export interface PlanResponseWire {
  items: PlanItemWire[];
}

// Share endpoints hit the conversations service directly; they don't need
// the analysis-agent layer (no agent state, no app_id resolution).
export const conversationSharePath = (id: string) => `${VITE_CONVERSATIONS_API}/${id}/share`;

export type ConversationStatus = "READY" | "MESSAGE_IN_PROGRESS";

export interface ConversationConnect {
  state: Record<string, unknown>;
  status: ConversationStatus;
  messages: Message[];
  agent: PartialAgent | null;
  // Names of the dataset(s) backing this conversation's context, surfaced as
  // the composer's folder indicator. A single entry today; a list so the UI
  // already handles the multi-dataset working set.
  dataset_names: string[];
}

export interface UpdateConversationInput {
  display_name?: string;
}

export interface ConversationSummary {
  id: string;
  display_name: string;
}

export async function updateConversation(
  id: string,
  input: UpdateConversationInput
): Promise<ConversationSummary> {
  return apiMutate<ConversationSummary>(conversationPath(id), "PATCH", input);
}

// Retitle a conversation from its transcript. 400 when the conversation is
// too thin to name — the caller surfaces that as a message, not a failure.
export async function generateConversationName(id: string): Promise<ConversationSummary> {
  return apiMutate<ConversationSummary>(conversationNamePath(id), "POST");
}

export async function deleteConversation(id: string): Promise<void> {
  await apiMutate<null>(conversationPath(id), "DELETE", undefined, { expects204: true });
}

// Signal cancellation of an in-flight run. The orchestrator picks up the
// Redis signal and unwinds; the SSE stream completes naturally and the
// agent emits RUN_FINISHED, so callers don't need to abort locally.
export async function cancelConversation(id: string): Promise<void> {
  await apiMutate<null>(conversationCancelPath(id), "POST", undefined, { expects204: true });
}

export interface ConversationShare {
  conversation_id: string;
  token: string;
  created_by: string;
  created_at: string;
}

// Returns null when no share exists for the conversation. Any other error
// (including network failures) still throws, so the caller can distinguish
// "no link yet" from "the request failed."
export async function getShareLink(conversationId: string): Promise<ConversationShare | null> {
  try {
    return await apiClient<ConversationShare>(conversationSharePath(conversationId));
  } catch (err) {
    if (isApiError(err) && (err as ApiError).status === 404) return null;
    throw err;
  }
}

export async function createShareLink(conversationId: string): Promise<ConversationShare> {
  return apiMutate<ConversationShare>(conversationSharePath(conversationId), "POST");
}

export async function deleteShareLink(conversationId: string): Promise<void> {
  await apiMutate<null>(conversationSharePath(conversationId), "DELETE", undefined, {
    expects204: true,
  });
}

// Platform conversations belong to the user, not a dataset, so they are found by app.
export const PLATFORM_AGENT_APP_ID = "PLATFORM_AGENT";

// Enough of the list to find the newest session among any the user still has
// runs writing to.
const PLATFORM_SESSION_WINDOW = 10;

// The caller's session is the platform conversation they opened last. The list
// endpoint sorts by `updated_at` and takes no sort parameter, so an abandoned
// session whose run is still writing would otherwise outrank the fresh one.
export async function activePlatformConversationId(signal?: AbortSignal): Promise<string | null> {
  const params = new URLSearchParams({
    app_id: PLATFORM_AGENT_APP_ID,
    limit: String(PLATFORM_SESSION_WINDOW),
  });
  const data = await apiClient<{ items: { id: string; created_at: string }[] }>(
    `${VITE_ANALYSIS_AGENT_API}/conversations?${params}`,
    { signal, onErrorMessage: () => "Failed to load the help session" }
  );
  const newest = data.items.reduce<{ id: string; created_at: string } | null>(
    (best, item) =>
      best && Date.parse(best.created_at) >= Date.parse(item.created_at) ? best : item,
    null
  );
  return newest?.id ?? null;
}

export async function createPlatformConversation(signal?: AbortSignal): Promise<string> {
  const data = await apiMutate<{ id: string }>(
    `${VITE_ANALYSIS_AGENT_API}/conversations`,
    "POST",
    {
      app_id: PLATFORM_AGENT_APP_ID,
      user_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    },
    { signal, onErrorMessage: () => "Failed to start a help session" }
  );
  if (!data) {
    throw new Error("Conversation create returned no body");
  }
  return data.id;
}
